/**
 * bills.tracked_recent — most recently tracked bills with sponsor + last
 * action. Lets the dashboard surface "what's the workspace following
 * right now" without making the user click into /bills.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

function formatRef(ref: string): string {
  const m = ref.match(/^(\d+)-(hr|s|hres|sres|hjres|sjres|hconres|sconres)-(\d+)$/);
  if (!m) return ref;
  const map: Record<string, string> = {
    hr: "H.R.", s: "S.", hres: "H.Res.", sres: "S.Res.",
    hjres: "H.J.Res.", sjres: "S.J.Res.", hconres: "H.Con.Res.", sconres: "S.Con.Res.",
  };
  return `${map[m[2]] || m[2]} ${m[3]}`;
}

export function BillsTrackedRecent({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 6;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "bills.tracked_recent", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/tracked-bills`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Tracked bills could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" aria-live="polite" className="space-y-2"><span className="sr-only">Loading…</span><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /><Skeleton className="h-3 w-3/5" /></div>;
  const tracked: any[] = data?.tracked_bills ?? [];
  if (tracked.length === 0) {
    return <div className="text-xs text-stone-600">No bills tracked yet. <Link to="/discover" className="font-medium text-action hover:underline">Find legislation to follow</Link>, or ask your AI client to track a bill.</div>;
  }

  const recent = [...tracked]
    .sort((a, b) => new Date(b.tracked_since || 0).getTime() - new Date(a.tracked_since || 0).getTime())
    .slice(0, limit);

  return (
    <ul className="space-y-2 text-xs">
      {recent.map((b) => {
        const meta = b.bill_meta || {};
        return (
          <li key={b.bill_ref} className="border-b border-stone-100 pb-1.5 last:border-b-0 last:pb-0">
            <Link to={`/bills/${encodeURIComponent(b.bill_ref)}`} className="block hover:bg-stone-50 px-1 py-0.5 rounded -mx-1">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[11px] text-stone-500">{formatRef(b.bill_ref)}</span>
                <span className="text-stone-700 truncate flex-1 text-[12px]">{meta.title || b.project_name || b.bill_ref}</span>
              </div>
              {meta.sponsor_name && (
                <div className="text-[10px] text-stone-400 mt-0.5 truncate">
                  {meta.sponsor_party && <span className="font-bold mr-1">{meta.sponsor_party.charAt(0)}</span>}
                  {meta.sponsor_name} · {meta.cosponsor_count ?? 0} cosponsors
                </div>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
